/**
 * Busca "LIKE PHP" para as listagens do sistema — a MESMA no cliente e no servidor.
 *
 * Nasceu em `apps/web/src/lib/text-search.ts` e mora aqui desde 02/09/2026 por
 * um motivo concreto: enquanto a semântica existia só no cliente, cada tela que
 * paginava no servidor tinha que escolher entre paginar de verdade e buscar
 * bem. A rota de servidores da prefeitura, por exemplo, casava `q` contra
 * `nome`/`cpfMasked`/`matricula` com um `includes` simples — sem acento, sem
 * multi-termo, sem normalizar dígitos, e com o `q` em minúsculas comparado
 * contra `matricula` em maiúsculas. Ligar o recorte no servidor com essa
 * divergência faria a busca procurar só dentro da página: o operador digita um
 * nome que existe e a tela diz que não existe, **sem erro nenhum**.
 *
 * `@atlas/types` é a casa porque é o único pacote-folha que API e web já
 * importam, e há precedente de helper puro executável aqui (`limitarRecorte`
 * em `paginacao.ts`).
 *
 * Regras:
 * - Case-insensitive e sem acento (Joao acha João).
 * - Multi-termo AND: split por espaco, todos os termos precisam bater.
 * - Varre APENAS campos primitivos de PRIMEIRO NIVEL do objeto — NAO recursa
 *   em subojetos (camposCustom, contratos etc). Isso evita falso-positivo
 *   tipo "trabalhando" em camposCustom.saffdsgfefe fazer aparecer servidores
 *   cuja SituacaoFuncional NAO e' trabalhando. Se precisar buscar num campo
 *   aninhado, achatar antes ou passar `keys` explicitas via matchAnyKeys.
 * - Termo com ou sem pontuacao — CPF "375.342.333-00", telefone
 *   "(11) 99999-0000", matricula "852-029" casam contra versao so-digitos.
 *   Antes exigia digitos puros; cliente reportou 23/07/2026 (CPF com pontos
 *   nao achava).
 *
 * ⚠️ No SERVIDOR, prefira sempre `matchAnyKeys` com chaves explicitas. Os
 * objetos de dominio da API nao sao os DTOs que saem na resposta:
 * `ServidorBuscaMock` carrega `passwordHash` como string de primeiro nivel, e
 * `matchAny` varre todo primitivo que encontra. As chaves explicitas sao o que
 * mantem hash de senha fora do alcance da busca.
 *
 * Uso:
 *   const filtered = lista.filter((row) => matchAny(row, query));
 *   const filtered = lista.filter((row) => matchAnyKeys(row, query, ["nome", "cpf"]));
 */
/** Combining Diacritical Marks (U+0300–U+036F) — o que sobra do NFD ao tirar o acento. */
const DIACRITICOS = /[̀-ͯ]/g;
function stripAccents(s) {
    return (s ?? "").normalize("NFD").replace(DIACRITICOS, "");
}
/** Extrai valores primitivos SO do primeiro nivel — sem recursao. Booleanos e
 *  null sao ignorados. Objetos aninhados (camposCustom, contratos) tambem —
 *  se precisa buscar neles, use matchAnyKeys passando chaves explicitas. */
function extrairPrimitivos(obj) {
    if (obj == null || typeof obj !== "object" || Array.isArray(obj))
        return [];
    const out = [];
    for (const v of Object.values(obj)) {
        if (typeof v === "string")
            out.push(v);
        else if (typeof v === "number" || typeof v === "bigint")
            out.push(String(v));
    }
    return out;
}
/** Monta o indice a partir dos valores textuais ja extraidos. */
function indiceDeValores(valores) {
    return {
        texto: stripAccents(valores.join(" ").toLowerCase()),
        digitos: valores.map((v) => String(v).replace(/\D/g, "")).filter(Boolean),
    };
}
/** Indice de um registro, restrito as chaves informadas. Use as MESMAS chaves
 *  na escrita e no `matchAnyKeys` equivalente — chaves diferentes sao a outra
 *  forma de divergir. */
export function indiceDeBusca(row, keys) {
    return indiceDeValores(valoresDasChaves(row, keys));
}
/**
 * Termo numerico digitado COM ou SEM mascara — CPF "375.342.333-00", telefone
 * "(11) 99999-0000", matricula "852-029". Devolve so os digitos, ou null se o
 * termo nao for numerico-com-pontuacao.
 *
 * Exportada porque a consulta em SQL precisa decidir, para cada termo, se vale
 * a pena comparar contra `digitos` — e essa decisao tem que ser a mesma aqui e
 * la.
 */
export function digitosDoTermo(t) {
    const d = t.replace(/\D/g, "");
    if (!d)
        return null;
    return /^[\d.\-/()\s]+$/.test(t) ? d : null;
}
/** Termos normalizados de uma query: sem acento, minusculos, sem repetidos.
 *  Query vazia devolve lista vazia (= casa com tudo). */
export function termosDaBusca(query) {
    const q = stripAccents(String(query ?? "").trim().toLowerCase());
    if (!q)
        return [];
    return termosUnicos(q);
}
/**
 * Casa um indice contra a query. **A definicao unica da semantica de busca.**
 *
 * `matchAny` e `matchAnyKeys` sao cascas sobre esta funcao, e a consulta em
 * SQL e a traducao mecanica dela: `texto LIKE` para a primeira metade,
 * `unnest(digitos) LIKE` para a segunda.
 */
export function matchIndice(idx, query) {
    const termos = termosDaBusca(query);
    return termos.every((t) => {
        if (idx.texto.includes(t))
            return true;
        const tDigitos = digitosDoTermo(t);
        if (tDigitos && idx.digitos.some((d) => d.includes(tDigitos)))
            return true;
        return false;
    });
}
function matchTermos(valores, query) {
    return matchIndice(indiceDeValores(valores), query);
}
/** Split + dedup de termos: "diego diego diego" -> ["diego"]. Sem dedup o
 *  every() so multiplica trabalho e nao muda resultado. */
function termosUnicos(q) {
    return Array.from(new Set(q.split(/\s+/).filter(Boolean)));
}
/** Retorna true se o objeto casa com a query (LIKE PHP). Query vazia -> true. */
export function matchAny(row, query) {
    return matchTermos(extrairPrimitivos(row), query);
}
/** Versao restrita: casa apenas nos campos informados. Usar quando o objeto
 *  tem campos textuais grandes (endereco, observacoes) que geram falso-positivo
 *  por substring — e SEMPRE no servidor, onde o objeto pode ter campos que nao
 *  saem na resposta (ver o aviso no topo do modulo). */
// T extends object (nao Record<string,unknown>) pra aceitar interfaces
// nominais como PrefeituraServidor sem index signature. Acesso via
// Record cast interno — keys ja e' restrito a keyof T pelo caller.
export function matchAnyKeys(row, query, keys) {
    return matchTermos(valoresDasChaves(row, keys), query);
}
/** Valores textuais das chaves informadas, na ordem. Booleano e null ficam de
 *  fora, como em `extrairPrimitivos`. */
function valoresDasChaves(row, keys) {
    const valores = [];
    for (const k of keys) {
        const v = row[k];
        if (typeof v === "string")
            valores.push(v);
        else if (typeof v === "number" || typeof v === "bigint")
            valores.push(String(v));
    }
    return valores;
}
//# sourceMappingURL=busca.js.map